import { Button, ScrollArea, Stack, Textarea } from '@mantine/core';
import { getHotkeyHandler } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { CommentItem } from 'app/Private/Comments';
import { Panel } from 'components/Panel';
import { Right } from 'components/Right';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'remeda';
import { useCreateDebtorComment } from 'shared/hooks/use-create-debtor-comment';
import { useLoadComments } from 'shared/hooks/use-load-comments';

export const DebtorNotes = () => {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { id: debtorId } = useParams<{ id: string }>();

  const { comments } = useLoadComments({ resourceType: 'debtor', resourceId: debtorId });
  const { createDebtorComment, isCreateDebtorCommentLoading } = useCreateDebtorComment();

  const [commentInput, setCommentInput] = useState('');
  const [commentBeingEdited, setCommentBeingEdited] = useState<number>();

  const viewport = useRef<HTMLDivElement>(null);

  const scrollToBottom = () =>
    viewport.current!.scrollTo({ top: viewport.current!.scrollHeight, behavior: 'smooth' });

  useEffect(() => {
    scrollToBottom();
    return () => {};
  }, [comments.length]);

  const handleSubmitComment = () => {
    createDebtorComment(
      { id: debtorId, comment: commentInput },
      { onSuccess: () => client.invalidateQueries({ queryKey: ['comments'] }) },
    );
    setCommentInput('');
  };

  return (
    <Panel title={t(i18nKeys.NOTES)} withBorder>
      <Stack>
        <ScrollArea.Autosize mah={400} viewportRef={viewport}>
          <Stack>
            {comments.map(comment => (
              <CommentItem
                key={comment.id}
                comment={comment}
                isBeingEdited={commentBeingEdited === comment.id}
                isEditingDisabled={commentBeingEdited !== undefined}
                setIsBeingEdited={(isBeingEdited: boolean) => {
                  setCommentBeingEdited(isBeingEdited ? comment.id : undefined);
                }}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>
        <Textarea
          value={commentInput}
          onChange={event => setCommentInput(event.currentTarget.value)}
          autosize
          radius={8}
          placeholder={t(i18nKeys.FORM.ADD_NOTE)}
          minRows={4}
          onKeyDown={getHotkeyHandler([['mod+Enter', handleSubmitComment]])}
          styles={{
            input: {
              borderColor: 'var(--mantine-color-gray-3)',
            },
          }}
        />
        <Right>
          <Button
            w="fit-content"
            variant="default"
            disabled={isEmpty(commentInput)}
            onClick={handleSubmitComment}
            loading={isCreateDebtorCommentLoading}
          >
            {t(i18nKeys.ACTIONS.ADD)}
          </Button>
        </Right>
      </Stack>
    </Panel>
  );
};
